import { useEffect } from 'react';
import { AppState, Platform } from 'react-native';
import { useAuthStore } from '@/store';

// Browsers/native timers cap delays at a signed 32-bit integer (about 25 days).
const MAX_DELAY_MS = 2147483647;

export default function useMonthlyCreditReset(resetDate, userId, sessionGeneration, authenticated) {
  useEffect(() => {
    const resetAt = resetDate ? new Date(resetDate).getTime() : NaN;
    if (!authenticated || !userId || !Number.isFinite(resetAt)) return;
    let disposed = false;
    let busy = false;
    let refreshed = false;
    let attempts = 0;
    let timer;
    const webDocument = Platform.OS === 'web' && typeof document !== 'undefined' ? document : null;
    const refreshIfDue = async () => {
      if (disposed || busy || refreshed || Date.now() < resetAt
        || (Platform.OS === 'web' ? webDocument?.visibilityState === 'hidden' : AppState.currentState !== 'active')) return;
      const auth = useAuthStore.getState();
      if (!auth.token || auth.user?.id !== userId || auth._sessionGeneration !== sessionGeneration) return;
      busy = true;
      attempts++;
      try {
        const credits = await auth.fetchUserCredits?.({ force: true });
        // A request already in flight before midnight can return the old cycle.
        refreshed = !!credits && (!credits.monthlyAllotment?.total
          || new Date(credits.monthlyAllotment.resetDate).getTime() > resetAt);
      } catch (_) { /* Retry briefly, then on the next foreground event. */ }
      finally {
        busy = false;
        if (!disposed && !refreshed && attempts < 3) timer = setTimeout(refreshIfDue, 1000);
      }
    };
    const arm = () => {
      if (disposed) return;
      if (Date.now() >= resetAt) { void refreshIfDue(); return; }
      timer = setTimeout(arm, Math.min(MAX_DELAY_MS, resetAt - Date.now()));
    };
    const onForeground = () => {
      if (busy || refreshed || Date.now() < resetAt) return;
      clearTimeout(timer);
      attempts = 0;
      void refreshIfDue();
    };
    // A sleeping/background client catches up when it returns to the foreground.
    const listener = Platform.OS === 'web'
      ? (webDocument?.addEventListener('visibilitychange', onForeground), null)
      : AppState.addEventListener('change', onForeground);
    arm();
    return () => {
      disposed = true;
      clearTimeout(timer);
      if (Platform.OS === 'web') webDocument?.removeEventListener('visibilitychange', onForeground);
      else listener?.remove();
    };
  }, [resetDate, userId, sessionGeneration, authenticated]);
}
